import { useEffect } from 'react';
import { DataList } from '@nimbus-ds/patterns';
import { useTranslation } from 'react-i18next';
import { Domain } from '@tiendanube/common/src/enums';
import { Card } from '@tiendanube/components';
import { BoxPackedIcon } from '@tiendanube/icons';
import { useNavegate } from 'App/hooks';
import useCustomerPriceTables from 'domains/Customers/CustomerPriceTables/hooks/useCustomerPriceTables';
import CustomerPriceTablesEmptyState from 'domains/Customers/Customers/pages/CustomerDetailsPage/components/CustomerPriceTables/components/CustomerPriceTablesEmptyState';
import CustomerPriceTablesErrorState from 'domains/Customers/Customers/pages/CustomerDetailsPage/components/CustomerPriceTables/components/CustomerPriceTablesErrorState';
import CustomerPriceTablesItem from 'domains/Customers/Customers/pages/CustomerDetailsPage/components/CustomerPriceTables/components/CustomerPriceTablesItem';
import CustomerPriceTablesSkeleton from 'domains/Customers/Customers/pages/CustomerDetailsPage/components/CustomerPriceTables/components/CustomerPriceTablesSkeleton';

interface CustomerPriceTablesProps {
  customerId: string;
}

export default function CustomerPriceTables({
  customerId,
}: Readonly<CustomerPriceTablesProps>) {
  const { t } = useTranslation(Domain.CUSTOMERS);
  const tPath = 'customerDetails.customerPriceTables';
  const { goTo } = useNavegate();

  const {
    customerPriceTablesStatus,
    customerPriceTablesData,
    fetchCustomerPriceTables,
    resetCustomerPriceTables,
  } = useCustomerPriceTables(customerId);

  useEffect(() => {
    fetchCustomerPriceTables();

    return () => {
      resetCustomerPriceTables();
    };
  }, [fetchCustomerPriceTables, resetCustomerPriceTables]);

  const headerLabel = customerPriceTablesData?.length
    ? {
        id: 'priceTablesHeaderLabel',
        label: t(`${tPath}.label`),
        appearance: 'primary' as const,
        icon: BoxPackedIcon,
      }
    : undefined;

  if (
    customerPriceTablesStatus.isSuccess &&
    customerPriceTablesData.length === 0
  ) {
    return (
      <CustomerPriceTablesEmptyState
        headerLabel={headerLabel}
        onClick={() => goTo(`/customers/edit/${customerId}`)}
      />
    );
  }

  if (customerPriceTablesStatus.isError) {
    return (
      <CustomerPriceTablesErrorState
        headerLabel={headerLabel}
        onRetry={fetchCustomerPriceTables}
      />
    );
  }

  if (customerPriceTablesStatus.isLoading) {
    return <CustomerPriceTablesSkeleton headerLabel={headerLabel} />;
  }

  return (
    <Card title={t(`${tPath}.title`)} headerLabel={headerLabel}>
      <div style={{ marginLeft: '-1rem', marginRight: '-1rem' }}>
        <DataList>
          {customerPriceTablesData.map((priceTable) => (
            <CustomerPriceTablesItem
              priceTable={priceTable}
              key={priceTable.id}
            />
          ))}
        </DataList>
      </div>
    </Card>
  );
}
